import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { isSubscriptionBlockedError, checkSubscriptionActive } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  CircularProgress,
  Typography,
  IconButton,
  Box,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchActiveMembers,
  fetchTeams,
  setSelectedTeam,
} from "../../../redux/Slice/Settings/settingsSlice";
import { addTeamMemberToTeam } from "../../../redux/Slice/Settings/addfunctionsSlice";

const AddToTeam = ({ open, onClose }) => {
  const dispatch = useDispatch();

  // Fetch the active members and selected team from Redux state
  const activeMembers = useSelector((state) => state.settings.activeMembers);
  const { selectedTeam } = useSelector((state) => state.settings);
  const loading = useSelector((state) => state.settings.loading);

  const [addingMember, setAddingMember] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      dispatch(fetchActiveMembers()); // Fetch active members when the dialog opens
    }
  }, [open, dispatch]);

  // Ensure selectedTeam exists
  if (!selectedTeam) return null;

  const teamId = selectedTeam._id;

  // Filter out members that are already in the selected team
  const availableMembers = activeMembers?.filter((member) => {
    return selectedTeam.members?.every(
      (teamMember) => teamMember._id !== member._id
    );
  });

  // Filter members based on search query
  const filteredMembers = availableMembers?.filter((member) =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle member selection/deselection
  const handleToggleMemberSelection = (memberId) => {
    setSelectedMembers((prevSelected) =>
      prevSelected.includes(memberId)
        ? prevSelected.filter((id) => id !== memberId) // Deselect
        : [...prevSelected, memberId] // Select
    );
  };

  const handleAddMembers = async () => {
    if (!teamId) {
      console.error("Team ID is missing");
      return;
    }

    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }

    setAddingMember(true);

    try {
      // Dispatch the thunk to add the selected members to the team
      for (let memberId of selectedMembers) {
        await dispatch(addTeamMemberToTeam({ teamId, memberId }));
      }

      const teamsResult = await dispatch(fetchTeams()).unwrap();
      const refreshedTeam = Array.isArray(teamsResult)
        ? teamsResult.find((t) => String(t._id) === String(teamId))
        : null;
      if (refreshedTeam) {
        dispatch(setSelectedTeam(refreshedTeam));
      } else {
        dispatch(
          setSelectedTeam({
            ...selectedTeam,
            members: [
              ...selectedTeam.members,
              ...availableMembers.filter((member) =>
                selectedMembers.includes(member._id)
              ),
            ],
          })
        );
      }

      onClose(); // Close the dialog after successful add
      toast.success("Members added to team successfully.");
    } catch (error) {
      console.error("Error adding members:", error);
      if (!isSubscriptionBlockedError(error)) {
        toast.error(typeof error === 'string' ? error : "Failed to add members to team.");
      }
    } finally {
      setAddingMember(false);
    }
  };

  const handleModalClose = () => {
    setSelectedMembers([]);
    setSearchQuery("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: (theme) => theme.shadows[24],
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "bold",
          fontSize: "24px",
          padding: "14px",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Typography variant="h6">Add New Members</Typography>
        <IconButton onClick={handleModalClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "24px",
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <TextField
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
          />

          <List
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              padding: 0,
              // Decrease scrollbar size for all screen sizes
              scrollbarWidth: { xs: "thin", sm: "thin", md: "thin", lg: "thin", xl: "thin" }, // Firefox
              "&::-webkit-scrollbar": {
                width: { xs: "4px", sm: "4px", md: "3px", lg: "3px", xl: "3px" }, // Very thin scrollbar for md/lg/xl
              },
              "&::-webkit-scrollbar-track": {
                background: (theme) => theme.palette.background.default,
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: (theme) => theme.palette.divider,
                borderRadius: "4px",
                "&:hover": {
                  background: (theme) => theme.palette.action.hover,
                },
              },
            }}
          >
            {filteredMembers && filteredMembers.length > 0 ? (
              filteredMembers.map((member) => (
                <ListItem
                  key={member._id}
                  sx={{
                    borderRadius: "12px",
                    marginBottom: 1,
                    "&:hover": {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                  }}
                >
                  <Checkbox
                    checked={selectedMembers.includes(member._id)}
                    onChange={() => handleToggleMemberSelection(member._id)}
                    sx={{
                      color: (theme) => theme.palette.primary.main,
                      "&.Mui-checked": {
                        color: (theme) => theme.palette.primary.main,
                      },
                    }}
                  />
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          color: (theme) => theme.palette.text.primary,
                          fontWeight: selectedMembers.includes(member._id)
                            ? "bold"
                            : "normal",
                        }}
                      >
                        {member.name}
                      </Typography>
                    }
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        color: (theme) => theme.palette.text.secondary,
                        textAlign: "center",
                      }}
                    >
                      No available members to add
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </Box>
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          padding: "16px 24px",
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          component="button"
          onClick={handleModalClose}
          sx={{
            borderRadius: "20px",
            textTransform: "capitalize",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            color: (theme) => theme.palette.text.primary,
            backgroundColor: "transparent",
            padding: "8px 16px",
            cursor: "pointer",
            "&:hover": {
              borderColor: (theme) => theme.palette.primary.main,
            },
          }}
        >
          Cancel
        </Box>
        <Box
          component="button"
          onClick={handleAddMembers}
          disabled={addingMember || selectedMembers.length === 0 || loading}
          sx={{
            borderRadius: "20px",
            textTransform: "capitalize",
            backgroundColor: (theme) => theme.palette.primary.main,
            color: "white",
            border: "none",
            padding: "8px 16px",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: (theme) => theme.palette.primary.dark,
            },
            "&:disabled": {
              backgroundColor: (theme) => theme.palette.action.disabled,
              cursor: "not-allowed",
            },
          }}
        >
          {addingMember || loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Add Members"
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddToTeam;
